import React, { useEffect, useMemo, useRef, useState } from "react";
import { Table, Tag, Button, Select, DatePicker, Modal, Input, Spin, message } from "antd";
import { FilterOutlined, RiseOutlined, FallOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { MdPeople, MdInbox, MdAssignment, MdHexagon, MdCampaign, MdAllInclusive } from "react-icons/md";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  getDashboardSummary,
  getShippedOrdersByDateRange,
  getOtcBreakEvenByMonth,
  getScorecards,
  createScorecard,
  updateScorecard,
  deleteScorecard,
  type ScorecardResponse,
} from "../../utils/API";
import CommonHeader from "../../Components/commonHeader";
import PageLogoutButton from "../../Components/PageLogoutButton";

interface RecentOrder {
  key: string;
  photo: string;
  name: string;
  category: string;
  dateTime: string;
  quantity: number;
  price: string;
  status: string;
}

const PLACEHOLDER_IMAGES = [
  require("../../Assets/dashboard-1.png"),
  require("../../Assets/dashboard-2.png"),
  require("../../Assets/dashboard-3.png"),
];

// OTC snapshot helpers — keep parity with src/Pages/otc/otc.tsx
const formatCurrencyWhole = (value: number) =>
  `$${Math.round(Number(value) || 0).toLocaleString()}`;

const parseOtcCurrencyValue = (value: any) => {
  const normalized = String(value ?? "").replace(/[$,]/g, "");
  return Number.parseFloat(normalized) || 0;
};

const isTestUnlimited = (leadFrom: any) => {
  const v = String(leadFrom || "").trim().toLowerCase();
  return v === "test unlimited" || v === "testunl" || v === "testunlimited";
};

const isAdwords = (leadFrom: any) => {
  const v = String(leadFrom || "").trim().toLowerCase();
  return v === "addwords" || v === "adwords";
};

const normalizeLeadName = (leadFrom: any) => {
  const raw = String(leadFrom || "Other").trim() || "Other";
  return raw.toLowerCase() === "ebay" ? "EBay" : raw;
};

const LEAD_COLORS = [
  "#004aad",
  "#4FD1C5",
  "#FFB347",
  "#8B8BFF",
  "#F6779A",
  "#32E63B",
  "#9CA3AF",
];

type OtcPeriod = "today" | "week" | "month" | "year" | "custom";

type CustomRange = [dayjs.Dayjs, dayjs.Dayjs] | null;

const getPeriodRange = (period: OtcPeriod, custom: CustomRange = null) => {
  const now = dayjs();
  switch (period) {
    case "today":
      return { start: now.startOf("day"), end: now.endOf("day") };
    case "week":
      return { start: now.startOf("week"), end: now.endOf("week") };
    case "year":
      return { start: now.startOf("year"), end: now.endOf("year") };
    case "custom":
      // Falls back to the default period until a range is actually picked.
      return custom
        ? { start: custom[0].startOf("day"), end: custom[1].endOf("day") }
        : { start: now.startOf("year"), end: now.endOf("year") };
    case "month":
    default:
      return { start: now.startOf("month"), end: now.endOf("month") };
  }
};

const PERIOD_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
  { value: "year", label: "This Year" },
  { value: "custom", label: "Custom Range" },
];

const getPeriodLabel = (period: OtcPeriod, custom: CustomRange = null) => {
  if (period !== "custom") {
    return (
      PERIOD_OPTIONS.find((option) => option.value === period)?.label || "This Month"
    );
  }
  if (!custom) return "Custom Range";
  return `${custom[0].format("MM/DD/YYYY")} – ${custom[1].format("MM/DD/YYYY")}`;
};

// Compact form for the dropdown itself — drops the century, and the year too when
// both ends share it, so a custom range still fits a narrow control.
const getShortPeriodLabel = (period: OtcPeriod, custom: CustomRange = null) => {
  if (period !== "custom") return getPeriodLabel(period);
  if (!custom) return "Custom";
  const sameYear = custom[0].isSame(custom[1], "year");
  return `${custom[0].format(sameYear ? "MM/DD" : "MM/DD/YY")} – ${custom[1].format("MM/DD/YY")}`;
};

// Day buckets read fine up to ~2 months; wider custom ranges roll up to months.
const shouldBucketByMonth = (
  period: OtcPeriod,
  start: dayjs.Dayjs,
  end: dayjs.Dayjs,
) => period === "year" || end.diff(start, "day") > 62;

// The comparison window for a trend: the equivalent period immediately before this one.
// A custom range compares against the equally long window right before it.
const getPreviousRange = (
  period: OtcPeriod,
  start: dayjs.Dayjs,
  end: dayjs.Dayjs,
) => {
  if (period === "custom") {
    const spanDays = end.diff(start, "day") + 1;
    const prevEnd = start.subtract(1, "day").endOf("day");
    return { start: prevEnd.subtract(spanDays - 1, "day").startOf("day"), end: prevEnd };
  }
  const unit = (period === "today" ? "day" : period) as any;
  const prevStart = start.subtract(1, unit).startOf(unit);
  return { start: prevStart, end: prevStart.endOf(unit) };
};

const getCompareLabel = (period: OtcPeriod) => {
  switch (period) {
    case "today":
      return "vs yesterday";
    case "week":
      return "vs last week";
    case "year":
      return "vs last year";
    case "custom":
      return "vs previous period";
    case "month":
    default:
      return "vs last month";
  }
};

interface OtcRow {
  rawDate: string | null;
  gm: number;
  gs: number;
  leadFrom: string;
  status: string;
}

const fetchOtcRows = async (
  start: dayjs.Dayjs,
  end: dayjs.Dayjs,
): Promise<OtcRow[]> => {
  const rangeStart = start.format("YYYY-MM-DD");
  const rangeEnd = end.format("YYYY-MM-DD");

  // The shipped endpoint is month scoped, so request each month the range spans.
  const months: dayjs.Dayjs[] = [];
  let cursor = start.startOf("month");
  const lastMonth = end.startOf("month");
  while (cursor.isBefore(lastMonth) || cursor.isSame(lastMonth, "month")) {
    months.push(cursor);
    cursor = cursor.add(1, "month");
  }

  const responses = await Promise.all(
    months.map((month) =>
      getShippedOrdersByDateRange(
        month.startOf("month").format("YYYY-MM-DD"),
        month.endOf("month").format("YYYY-MM-DD"),
      ).catch(() => ({ data: [] })),
    ),
  );

  return responses
    .flatMap((res: any) => (Array.isArray(res?.data) ? res.data : []))
    .map((item: any) => ({
      rawDate: item.date ? dayjs(item.date).format("YYYY-MM-DD") : null,
      gm: parseOtcCurrencyValue(item.gross_margin),
      gs: parseOtcCurrencyValue(item.gross_sale),
      leadFrom: normalizeLeadName(item.lead_from),
      status: String(item.po_status || "").trim(),
    }))
    .filter((row) =>
      row.rawDate ? row.rawDate >= rangeStart && row.rawDate <= rangeEnd : false,
    );
};

const sumBreakEven = async (start: dayjs.Dayjs, end: dayjs.Dayjs) => {
  const months: dayjs.Dayjs[] = [];
  let cursor = start.startOf("month");
  const lastMonth = end.startOf("month");
  while (cursor.isBefore(lastMonth) || cursor.isSame(lastMonth, "month")) {
    months.push(cursor);
    cursor = cursor.add(1, "month");
  }

  const values = await Promise.all(
    months.map((month) =>
      getOtcBreakEvenByMonth(month.month() + 1, month.year())
        .then((res: any) => {
          const monthStart = month.startOf("month");
          const monthEnd = month.endOf("month");
          // Prorate by the days of this month actually inside [start, end] — a
          // custom range covering only part of a month shouldn't be judged
          // against that month's full break-even target.
          const coveredStart = start.isAfter(monthStart) ? start : monthStart;
          const coveredEnd = end.isBefore(monthEnd) ? end : monthEnd;
          const coveredDays = coveredEnd.diff(coveredStart, "day") + 1;
          const totalDaysInMonth = monthEnd.diff(monthStart, "day") + 1;
          const fraction = coveredDays / totalDaysInMonth;
          return (Number(res?.breakEvenValue) || 0) * fraction;
        })
        .catch(() => 0),
    ),
  );

  return values.reduce((sum, value) => sum + value, 0);
};

// Quick presets offered inside the custom-range modal
const RANGE_PRESETS: Array<{ label: string; range: () => [dayjs.Dayjs, dayjs.Dayjs] }> = [
  { label: "Last 7 days", range: () => [dayjs().subtract(6, "day"), dayjs()] },
  { label: "Last 30 days", range: () => [dayjs().subtract(29, "day"), dayjs()] },
  {
    label: "Last month",
    range: () => [
      dayjs().subtract(1, "month").startOf("month"),
      dayjs().subtract(1, "month").endOf("month"),
    ],
  },
  {
    label: "Last 3 months",
    range: () => [dayjs().subtract(2, "month").startOf("month"), dayjs().endOf("month")],
  },
  {
    label: "Year to date",
    range: () => [dayjs().startOf("year"), dayjs()],
  },
];

// A single dropdown. Choosing "Custom Range" opens a modal to pick the dates;
// cancelling reverts to the previous period so the UI is never left half-set.
const PeriodSelector = ({
  period,
  onPeriodChange,
  customRange,
  onCustomRangeChange,
}: {
  period: OtcPeriod;
  onPeriodChange: (period: OtcPeriod) => void;
  customRange: CustomRange;
  onCustomRangeChange: (range: CustomRange) => void;
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [draftRange, setDraftRange] = useState<CustomRange>(null);

  const openModal = () => {
    setDraftRange(customRange || [dayjs().startOf("year"), dayjs().endOf("year")]);
    setIsModalOpen(true);
  };

  const handleApply = () => {
    if (!draftRange) return;
    onCustomRangeChange([draftRange[0].startOf("day"), draftRange[1].endOf("day")]);
    onPeriodChange("custom");
    setIsModalOpen(false);
  };

  return (
    <>
      <Select
        value={period}
        size="small"
        // Sized for the fixed labels; a custom range widens it just enough
        className={`flex-shrink-0 ${period === "custom" ? "w-[140px]" : "w-[110px]"}`}
        popupMatchSelectWidth={false}
        // Show the picked dates on the closed dropdown instead of a bare "Custom Range"
        optionLabelProp="selectedLabel"
        // onSelect fires on every click, even re-picking "custom" while already
        // on custom — onChange alone would skip that since the value doesn't change.
        onSelect={(value) => {
          if (value === "custom") {
            openModal();
          }
        }}
        onChange={(value) => {
          if (value === "custom") {
            return; // period is committed on Apply, not on select
          }
          onPeriodChange(value);
        }}
        options={PERIOD_OPTIONS.map((option) => ({
          ...option,
          selectedLabel:
            option.value === "custom"
              ? getShortPeriodLabel("custom", customRange)
              : option.label,
        }))}
      />

      <Modal
        title="Select Custom Date Range"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={handleApply}
        okText="Apply"
        okButtonProps={{ disabled: !draftRange, className: "bg-[#004aad]" }}
        // Wide enough for the two-panel calendar to open without clipping
        width={640}
        destroyOnClose
      >
        <div className="py-2">
          {/* Presets first, so the calendar popup opens downward into empty space */}
          <p className="text-[13px] font-medium text-gray-600 mb-1.5">Quick select</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {RANGE_PRESETS.map((preset) => {
              const [presetStart, presetEnd] = preset.range();
              const isActive =
                !!draftRange &&
                draftRange[0].isSame(presetStart, "day") &&
                draftRange[1].isSame(presetEnd, "day");
              return (
                <Button
                  key={preset.label}
                  size="small"
                  type={isActive ? "primary" : "default"}
                  className={isActive ? "!bg-[#004aad]" : ""}
                  onClick={() => setDraftRange([presetStart, presetEnd])}
                >
                  {preset.label}
                </Button>
              );
            })}
          </div>

          <p className="text-[13px] font-medium text-gray-600 mb-1.5">
            Or pick exact dates
          </p>
          <div
            // Auto-inserts "/" after MM and DD as the user types digits into
            // either date field, so typing "08122026" reads as "08/12/2026"
            // without the user having to type the slashes themselves.
            onInput={(e) => {
              const target = e.target as HTMLInputElement;
              if (target.tagName !== "INPUT") return;

              const digitsOnly = target.value.replace(/\D/g, "").slice(0, 8);
              let formatted = digitsOnly;
              if (digitsOnly.length > 4) {
                formatted = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2, 4)}/${digitsOnly.slice(4)}`;
              } else if (digitsOnly.length > 2) {
                formatted = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2)}`;
              }

              if (formatted !== target.value) {
                const cursorWasAtEnd = target.selectionStart === target.value.length;
                target.value = formatted;
                if (cursorWasAtEnd) {
                  target.setSelectionRange(formatted.length, formatted.length);
                }
              }
            }}
          >
            <DatePicker.RangePicker
              value={draftRange}
              format="MM/DD/YYYY"
              allowClear={false}
              placement="bottomLeft"
              size="large"
              className="w-full dashboard-range-picker"
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setDraftRange([dates[0], dates[1]]);
                }
              }}
              // Picks up a manually typed date as soon as it's valid, without
              // waiting for Enter/blur — onChange alone only fires on commit.
              onCalendarChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setDraftRange([dates[0], dates[1]]);
                }
              }}
            />
          </div>

          {draftRange ? (
            <p className="text-[13px] text-gray-500 mt-3">
              Showing{" "}
              <span className="font-semibold text-gray-700">
                {draftRange[0].format("MM/DD/YYYY")} – {draftRange[1].format("MM/DD/YYYY")}
              </span>{" "}
              ({draftRange[1].diff(draftRange[0], "day") + 1} days)
            </p>
          ) : null}
        </div>
      </Modal>
    </>
  );
};

const SCORECARD_PAGE_SIZE = 15;
const SCORECARD_ROW_HEIGHT = 44;

const ALL_MONTH_KEYS = [
  "jan", "feb", "mar", "apr", "may", "jun",
  "jul", "aug", "sep", "oct", "nov", "dec",
];

const SCORECARD_MONTH_COL_WIDTH = 76;
const SCORECARD_VISIBLE_MONTH_COLS = 4;

const WHO_OPTIONS = ["Sean", "Matinique", "TBD"];

type ScorecardRow = ScorecardResponse & { __blank?: boolean };

// Editable text cell: local draft state so typing doesn't re-render/re-fetch the
// whole table, saved to the backend only on blur (or Enter).
const EditableCell = ({
  value,
  onSave,
}: {
  value: string;
  onSave: (next: string) => void;
}) => {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const commit = () => {
    if (draft !== value) onSave(draft);
  };

  return (
    <Input
      value={draft}
      bordered={false}
      style={{ textAlign: "left", padding: 0 }}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onPressEnter={(e) => (e.target as HTMLInputElement).blur()}
    />
  );
};

const ScorecardWidget = () => {
  const [rows, setRows] = useState<ScorecardResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const infoTableWrapRef = useRef<HTMLDivElement>(null);
  const monthTableWrapRef = useRef<HTMLDivElement>(null);
  const actionTableWrapRef = useRef<HTMLDivElement>(null);

  // Info / month / action are three independent antd Tables so the month strip
  // can scroll horizontally on its own while Actions stays pinned; keep all
  // three vertical scroll positions in lockstep so rows still line up when
  // >15 rows trigger the shared vertical scrollbar.
  useEffect(() => {
    const bodies = [infoTableWrapRef, monthTableWrapRef, actionTableWrapRef]
      .map((ref) => ref.current?.querySelector<HTMLDivElement>(".ant-table-body"))
      .filter((el): el is HTMLDivElement => Boolean(el));
    if (bodies.length < 2) return;

    let syncing = false;
    const listeners = bodies.map((source) => {
      const onScroll = () => {
        if (syncing) return;
        syncing = true;
        bodies.forEach((target) => {
          if (target !== source) target.scrollTop = source.scrollTop;
        });
        syncing = false;
      };
      source.addEventListener("scroll", onScroll);
      return { source, onScroll };
    });

    return () => {
      listeners.forEach(({ source, onScroll }) => source.removeEventListener("scroll", onScroll));
    };
  }, [rows.length]);

  const loadScorecards = async () => {
    try {
      setLoading(true);
      const response = await getScorecards({ limit: 1000 });
      setRows(Array.isArray(response?.data) ? response.data : []);
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to load scorecard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadScorecards();
  }, []);

  const handleAdd = async () => {
    try {
      const created = await createScorecard({
        who: "TBD",
        department: "",
        measurable: "",
        notes: "",
        target: "",
        months: {},
      });
      setRows((prev) => [created, ...prev]);
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to add scorecard entry");
    }
  };

  const handleFieldSave = async (row: ScorecardResponse, field: string, value: string) => {
    try {
      const payload = {
        who: row.who,
        department: row.department,
        measurable: row.measurable,
        notes: row.notes,
        target: row.target,
        months: row.months || {},
        [field]: value,
      };
      const updated = await updateScorecard(row.id, payload);
      setRows((prev) => prev.map((r) => (r.id === row.id ? updated : r)));
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to save scorecard entry");
    }
  };

  const handleMonthSave = async (row: ScorecardResponse, monthKey: string, value: string) => {
    try {
      const months = { ...(row.months || {}), [monthKey]: value };
      const updated = await updateScorecard(row.id, {
        who: row.who,
        department: row.department,
        measurable: row.measurable,
        notes: row.notes,
        target: row.target,
        months,
      });
      setRows((prev) => prev.map((r) => (r.id === row.id ? updated : r)));
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to save scorecard entry");
    }
  };

  const handleDelete = async (row: ScorecardResponse) => {
    try {
      await deleteScorecard(row.id);
      message.success("Scorecard entry deleted");
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to delete scorecard entry");
    }
  };

  const infoColumns: ColumnsType<ScorecardRow> = [
    {
      title: "Who",
      dataIndex: "who",
      key: "who",
      width: 90,
      render: (_: any, record: ScorecardRow) =>
        record.__blank ? null : (
          <Select
            size="small"
            value={record.who || "TBD"}
            bordered={false}
            style={{ width: "100%" }}
            popupMatchSelectWidth={false}
            options={WHO_OPTIONS.map((option) => ({ label: option, value: option }))}
            onChange={(value) => handleFieldSave(record, "who", value)}
          />
        ),
    },
    {
      title: "Department",
      dataIndex: "department",
      key: "department",
      width: 100,
      render: (_: any, record: ScorecardRow) =>
        record.__blank ? null : (
          <EditableCell
            value={record.department || ""}
            onSave={(next) => handleFieldSave(record, "department", next)}
          />
        ),
    },
    {
      title: "Measureable",
      dataIndex: "measurable",
      key: "measurable",
      render: (_: any, record: ScorecardRow) =>
        record.__blank ? null : (
          <EditableCell
            value={record.measurable || ""}
            onSave={(next) => handleFieldSave(record, "measurable", next)}
          />
        ),
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      render: (_: any, record: ScorecardRow) =>
        record.__blank ? null : (
          <EditableCell
            value={record.notes || ""}
            onSave={(next) => handleFieldSave(record, "notes", next)}
          />
        ),
    },
    {
      title: "Target",
      dataIndex: "target",
      key: "target",
      width: 70,
      render: (_: any, record: ScorecardRow) =>
        record.__blank ? null : (
          <EditableCell
            value={record.target || ""}
            onSave={(next) => handleFieldSave(record, "target", next)}
          />
        ),
    },
  ];

  // All 12 months always shown; this table renders in its own horizontally
  // scrollable strip so only SCORECARD_VISIBLE_MONTH_COLS are visible at once.
  const monthColumns: ColumnsType<ScorecardRow> = ALL_MONTH_KEYS.map((monthKey) => ({
    title: monthKey.charAt(0).toUpperCase() + monthKey.slice(1),
    key: monthKey,
    width: SCORECARD_MONTH_COL_WIDTH,
    render: (_: any, record: ScorecardRow) =>
      record.__blank ? null : (
        <EditableCell
          value={record.months?.[monthKey] || ""}
          onSave={(next) => handleMonthSave(record, monthKey, next)}
        />
      ),
  }));

  // Rendered as its own fixed table after the scrollable month strip, so the
  // Delete action is always visible regardless of month-scroll position.
  const actionColumns: ColumnsType<ScorecardRow> = [
    {
      title: "Actions",
      key: "actions",
      width: 70,
      align: "center" as const,
      render: (_: any, record: ScorecardRow) =>
        record.__blank ? null : (
          <Button
            size="small"
            danger
            type="text"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          />
        ),
    },
  ];

  // Latest records first, capped to the default page size; pad short lists with
  // blank rows so the table always reserves 15 rows worth of vertical space.
  const displayRows = rows.slice(0, SCORECARD_PAGE_SIZE);
  const paddingRows = Math.max(SCORECARD_PAGE_SIZE - displayRows.length, 0);
  const paddedDataSource = [
    ...displayRows,
    ...Array.from({ length: paddingRows }, (_, index) => ({
      id: -1 * (index + 1),
      __blank: true,
    })),
  ] as ScorecardRow[];

  return (
    <div className="bg-white rounded-[24px] border border-gray-200 p-6 shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-800"></h2>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
          className="bg-[#004aad] rounded-lg px-6 h-9 font-medium shadow-sm"
        >
          Add
        </Button>
      </div>
      <Spin spinning={loading}>
        <div className="rounded-xl overflow-hidden border border-gray-50 flex">
          <div className="flex-1 min-w-0" ref={infoTableWrapRef}>
            <Table
              dataSource={paddedDataSource}
              columns={infoColumns}
              rowKey="id"
              pagination={false}
              className="custom-dashboard-table scorecard-table"
              rowClassName={(record: any) => (record.__blank ? "h-11 opacity-0" : "h-11")}
              scroll={rows.length > SCORECARD_PAGE_SIZE ? { y: SCORECARD_PAGE_SIZE * SCORECARD_ROW_HEIGHT } : undefined}
            />
          </div>
          <div
            className="flex-shrink-0 overflow-x-auto border-l border-gray-100"
            style={{ width: SCORECARD_MONTH_COL_WIDTH * SCORECARD_VISIBLE_MONTH_COLS }}
            ref={monthTableWrapRef}
          >
            <Table
              dataSource={paddedDataSource}
              columns={monthColumns}
              rowKey="id"
              pagination={false}
              className="custom-dashboard-table scorecard-table"
              rowClassName={(record: any) => (record.__blank ? "h-11 opacity-0" : "h-11")}
              scroll={{
                x: SCORECARD_MONTH_COL_WIDTH * ALL_MONTH_KEYS.length,
                ...(rows.length > SCORECARD_PAGE_SIZE
                  ? { y: SCORECARD_PAGE_SIZE * SCORECARD_ROW_HEIGHT }
                  : {}),
              }}
            />
          </div>
          <div className="flex-shrink-0 border-l border-gray-100" ref={actionTableWrapRef}>
            <Table
              dataSource={paddedDataSource}
              columns={actionColumns}
              rowKey="id"
              pagination={false}
              className="custom-dashboard-table scorecard-table"
              rowClassName={(record: any) => (record.__blank ? "h-11 opacity-0" : "h-11")}
              scroll={rows.length > SCORECARD_PAGE_SIZE ? { y: SCORECARD_PAGE_SIZE * SCORECARD_ROW_HEIGHT } : undefined}
            />
          </div>
        </div>
      </Spin>
    </div>
  );
};

const Dashboard = () => {
  const [loading, setLoading] = useState(false);
  const [revenuePeriod, setRevenuePeriod] = useState<OtcPeriod>("year");
  const [revenueCustomRange, setRevenueCustomRange] = useState<CustomRange>(null);
  const [statusPeriod, setStatusPeriod] = useState<OtcPeriod>("year");
  const [statusCustomRange, setStatusCustomRange] = useState<CustomRange>(null);
  const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([]);

  // OTC snapshot state
  const [cardPeriod, setCardPeriod] = useState<OtcPeriod>("year");
  const [cardCustomRange, setCardCustomRange] = useState<CustomRange>(null);
  const [otcCardRows, setOtcCardRows] = useState<OtcRow[]>([]);
  const [otcPrevGm, setOtcPrevGm] = useState(0);
  const [otcPrevGs, setOtcPrevGs] = useState(0);
  const [otcPrevAdwordsGm, setOtcPrevAdwordsGm] = useState(0);
  const [otcPrevTestUnlimitedGm, setOtcPrevTestUnlimitedGm] = useState(0);
  const [otcBreakEven, setOtcBreakEven] = useState(0);
  const [gmTrendRows, setGmTrendRows] = useState<OtcRow[]>([]);
  const [gmTrendPrevGm, setGmTrendPrevGm] = useState(0);
  const [leadRows, setLeadRows] = useState<OtcRow[]>([]);

  useEffect(() => {
    const loadBase = async () => {
      try {
        setLoading(true);
        const summary = await getDashboardSummary({ period: "month" });

        const mappedOrders: RecentOrder[] = Array.isArray(summary?.recentOrders)
          ? summary.recentOrders.map((item, index) => ({
              key: String(item.key || index + 1),
              photo: PLACEHOLDER_IMAGES[index % PLACEHOLDER_IMAGES.length],
              name: String(item.name || "-"),
              category: String(item.category || "-"),
              dateTime: item.dateTime
                ? new Date(item.dateTime).toLocaleString("en-US", {
                    month: "2-digit",
                    day: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "-",
              quantity: Number(item.quantity || 0),
              price: String(item.price || "$0.00"),
              status: String(item.status || "Pending"),
            }))
          : [];
        setRecentOrders(mappedOrders);
      } catch (error: any) {
        message.error(error?.response?.data?.message || "Failed to load dashboard");
      } finally {
        setLoading(false);
      }
    };

    loadBase();
  }, []);

  // Stat cards — OTC snapshot totals for the selected period + previous period for trend
  useEffect(() => {
    if (cardPeriod === "custom" && !cardCustomRange) return;

    const loadOtcCards = async () => {
      try {
        const { start, end } = getPeriodRange(cardPeriod, cardCustomRange);
        const prev = getPreviousRange(cardPeriod, start, end);

        const [rows, prevRows, breakEven] = await Promise.all([
          fetchOtcRows(start, end),
          fetchOtcRows(prev.start, prev.end),
          sumBreakEven(start, end),
        ]);

        setOtcCardRows(rows);
        setOtcPrevGm(prevRows.reduce((sum, row) => sum + row.gm, 0));
        setOtcPrevGs(prevRows.reduce((sum, row) => sum + row.gs, 0));
        setOtcPrevAdwordsGm(
          prevRows.reduce((sum, row) => (isAdwords(row.leadFrom) ? sum + row.gm : sum), 0),
        );
        setOtcPrevTestUnlimitedGm(
          prevRows.reduce((sum, row) => (isTestUnlimited(row.leadFrom) ? sum + row.gm : sum), 0),
        );
        setOtcBreakEven(breakEven);
      } catch (error: any) {
        message.error(
          error?.response?.data?.message || "Failed to load OTC snapshot totals",
        );
      }
    };
    loadOtcCards();
  }, [cardPeriod, cardCustomRange]);

  // Gross Margin over time — OTC snapshot GM bucketed across the selected period
  useEffect(() => {
    if (revenuePeriod === "custom" && !revenueCustomRange) return;

    const loadGmTrend = async () => {
      try {
        const { start, end } = getPeriodRange(revenuePeriod, revenueCustomRange);
        const prev = getPreviousRange(revenuePeriod, start, end);

        const [rows, prevRows] = await Promise.all([
          fetchOtcRows(start, end),
          fetchOtcRows(prev.start, prev.end),
        ]);

        setGmTrendRows(rows);
        setGmTrendPrevGm(prevRows.reduce((sum, row) => sum + row.gm, 0));
      } catch (error: any) {
        message.error(
          error?.response?.data?.message || "Failed to load gross margin trend",
        );
      }
    };
    loadGmTrend();
  }, [revenuePeriod, revenueCustomRange]);

  // Lead-wise GM breakdown — same grouping the OTC snapshot sidebar uses
  useEffect(() => {
    if (statusPeriod === "custom" && !statusCustomRange) return;

    const loadLeadBreakdown = async () => {
      try {
        const { start, end } = getPeriodRange(statusPeriod, statusCustomRange);
        setLeadRows(await fetchOtcRows(start, end));
      } catch (error: any) {
        message.error(
          error?.response?.data?.message || "Failed to load lead breakdown",
        );
      }
    };
    loadLeadBreakdown();
  }, [statusPeriod, statusCustomRange]);

  const columns: ColumnsType<RecentOrder> = [
    {
      title: "Product Name",
      dataIndex: "name",
      key: "name",
      onHeaderCell: () => ({
        style: { backgroundColor: "#F0F7FF", color: "#374151", fontWeight: "500" },
      }),
      render: (text: any, record: RecentOrder) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full overflow-hidden border border-gray-100 flex-shrink-0">
            <img src={record.photo} alt={String(text)} className="w-full h-full object-cover" />
          </div>
          <span className="font-medium text-gray-800">{text}</span>
        </div>
      ),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      onHeaderCell: () => ({
        style: { backgroundColor: "#F0F7FF", color: "#374151", fontWeight: "500" },
      }),
      render: (text: any) => <span className="text-gray-600">{text}</span>,
    },
    {
      title: "Date - Time",
      dataIndex: "dateTime",
      key: "dateTime",
      onHeaderCell: () => ({
        style: { backgroundColor: "#F0F7FF", color: "#374151", fontWeight: "500" },
      }),
      render: (text: any) => <span className="text-gray-500 font-normal">{text}</span>,
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      align: "center",
      onHeaderCell: () => ({
        style: { backgroundColor: "#F0F7FF", color: "#374151", fontWeight: "500" },
      }),
      render: (text: any) => <span className="text-gray-400">{text}</span>,
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      onHeaderCell: () => ({
        style: { backgroundColor: "#F0F7FF", color: "#374151", fontWeight: "500" },
      }),
      render: (text: any) => <span className="text-gray-800 font-semibold">{text}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      onHeaderCell: () => ({
        style: { backgroundColor: "#F0F7FF", color: "#374151", fontWeight: "500" },
      }),
      render: (status: any) => {
        const statusText = String(status || "").toLowerCase();
        const deliveredLike =
          statusText.includes("delivered") ||
          statusText.includes("closed") ||
          statusText.includes("done");
        const colorClass = deliveredLike
          ? "bg-[#E6F9F4] text-[#4FD1C5]"
          : "bg-[#FFF5EB] text-[#F6AD55]";
        return (
          <Tag className={`border-none rounded-full px-4 py-0.5 font-medium ${colorClass}`}>
            {status}
          </Tag>
        );
      },
    },
  ];

  const percentChange = (current: number, previous: number) => {
    if (!previous) return current ? 100 : 0;
    return ((current - previous) / Math.abs(previous)) * 100;
  };

  const totalGM = useMemo(
    () => otcCardRows.reduce((sum, row) => sum + row.gm, 0),
    [otcCardRows],
  );
  const totalGS = useMemo(
    () => otcCardRows.reduce((sum, row) => sum + row.gs, 0),
    [otcCardRows],
  );
  const testUnlimitedGM = useMemo(
    () =>
      otcCardRows.reduce(
        (sum, row) => (isTestUnlimited(row.leadFrom) ? sum + row.gm : sum),
        0,
      ),
    [otcCardRows],
  );
  const adwordsGM = useMemo(
    () =>
      otcCardRows.reduce(
        (sum, row) => (isAdwords(row.leadFrom) ? sum + row.gm : sum),
        0,
      ),
    [otcCardRows],
  );
  const currentPositionValue = totalGM - otcBreakEven;

  const gmTrend = percentChange(totalGM, otcPrevGm);
  const gmTrendIsUp = gmTrend >= 0;
  const gmTrendText = `${Math.abs(gmTrend).toFixed(1)}%`;

  const gsTrend = percentChange(totalGS, otcPrevGs);
  const gsTrendIsUp = gsTrend >= 0;
  const gsTrendText = `${Math.abs(gsTrend).toFixed(1)}%`;

  const adwordsTrend = percentChange(adwordsGM, otcPrevAdwordsGm);
  const adwordsTrendIsUp = adwordsTrend >= 0;
  const adwordsTrendText = `${Math.abs(adwordsTrend).toFixed(1)}%`;

  const testUnlimitedTrend = percentChange(testUnlimitedGM, otcPrevTestUnlimitedGm);
  const testUnlimitedTrendIsUp = testUnlimitedTrend >= 0;
  const testUnlimitedTrendText = `${Math.abs(testUnlimitedTrend).toFixed(1)}%`;

  const compareLabel = getCompareLabel(cardPeriod);

  // Bucket GM by day for short periods, by month for the year / wide custom ranges
  const revenueBucketByMonth = useMemo(() => {
    const { start, end } = getPeriodRange(revenuePeriod, revenueCustomRange);
    return shouldBucketByMonth(revenuePeriod, start, end);
  }, [revenuePeriod, revenueCustomRange]);

  const lineData = useMemo(() => {
    const { start, end } = getPeriodRange(revenuePeriod, revenueCustomRange);
    const byMonth = revenueBucketByMonth;
    const buckets: Array<{ name: string; key: string; gm: number }> = [];

    let cursor = byMonth ? start.startOf("month") : start.startOf("day");
    const last = byMonth ? end.startOf("month") : end.startOf("day");
    while (cursor.isBefore(last) || cursor.isSame(last, byMonth ? "month" : "day")) {
      buckets.push({
        name: byMonth
          ? cursor.format("MMM YY")
          : revenuePeriod === "custom"
            ? cursor.format("MM/DD")
            : cursor.format("D"),
        key: cursor.format(byMonth ? "YYYY-MM" : "YYYY-MM-DD"),
        gm: 0,
      });
      cursor = cursor.add(1, byMonth ? "month" : "day");
    }

    const index = new Map(buckets.map((bucket) => [bucket.key, bucket]));
    gmTrendRows.forEach((row) => {
      if (!row.rawDate) return;
      const key = byMonth ? row.rawDate.slice(0, 7) : row.rawDate;
      const bucket = index.get(key);
      if (bucket) bucket.gm += row.gm;
    });

    return buckets.map(({ name, gm }) => ({ name, gm }));
  }, [gmTrendRows, revenuePeriod, revenueCustomRange, revenueBucketByMonth]);

  const totalTrendGM = useMemo(
    () => lineData.reduce((sum, item) => sum + Number(item.gm || 0), 0),
    [lineData],
  );

  // The chart's own trend — driven by the chart's period, not the stat-card period
  const chartTrend = percentChange(totalTrendGM, gmTrendPrevGm);
  const chartTrendIsUp = chartTrend >= 0;
  const chartTrendText = `${Math.abs(chartTrend).toFixed(1)}%`;
  const chartCompareLabel = getCompareLabel(revenuePeriod);

  // Lead-wise GM totals — mirrors the OTC snapshot sidebar grouping
  const pieData = useMemo(() => {
    const totals = leadRows.reduce<Record<string, number>>((acc, row) => {
      acc[row.leadFrom] = (acc[row.leadFrom] || 0) + row.gm;
      return acc;
    }, {});

    return Object.entries(totals)
      .filter(([, value]) => value > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([name, value], index) => ({
        name,
        value: Math.round(value),
        color: LEAD_COLORS[index % LEAD_COLORS.length],
      }));
  }, [leadRows]);

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Same header the other pages use — page list dropdown + logout */}
      <CommonHeader
        className="mb-0"
        recordInputLabel="Records"
        rightContent={<PageLogoutButton />}
      />

      <div className="p-4 space-y-8">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <h2 className="text-xl font-semibold text-gray-800">Dashboard</h2>
        <PeriodSelector
          period={cardPeriod}
          onPeriodChange={setCardPeriod}
          customRange={cardCustomRange}
          onCustomRangeChange={setCardCustomRange}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
        <StatCard
          title="Addwords GM"
          periodLabel={getPeriodLabel(cardPeriod, cardCustomRange)}
          value={formatCurrencyWhole(adwordsGM)}
          trend={adwordsTrendText}
          trendText={compareLabel}
          isUp={adwordsTrendIsUp}
          icon={<MdCampaign />}
          iconBg="bg-[#FFF5E6]"
          iconColor="text-[#FFB347]"
        />
        <StatCard
          title="Test Unlimited GM"
          periodLabel={getPeriodLabel(cardPeriod, cardCustomRange)}
          value={formatCurrencyWhole(testUnlimitedGM)}
          trend={testUnlimitedTrendText}
          trendText={compareLabel}
          isUp={testUnlimitedTrendIsUp}
          icon={<MdAllInclusive />}
          iconBg="bg-[#E6F9F4]"
          iconColor="text-[#4FD1C5]"
        />
        <StatCard
          title="Gross Sales"
          periodLabel={getPeriodLabel(cardPeriod, cardCustomRange)}
          value={formatCurrencyWhole(totalGS)}
          trend={gsTrendText}
          trendText={compareLabel}
          isUp={gsTrendIsUp}
          icon={<MdInbox />}
          iconBg="bg-[#FFF5E6]"
          iconColor="text-[#FFB347]"
        />
        <StatCard
          title="Gross Margin"
          periodLabel={getPeriodLabel(cardPeriod, cardCustomRange)}
          value={formatCurrencyWhole(totalGM)}
          trend={gmTrendText}
          trendText={compareLabel}
          isUp={gmTrendIsUp}
          icon={<MdPeople />}
          iconBg="bg-[#EBEBFF]"
          iconColor="text-[#8B8BFF]"
        />
        <StatCard
          title="Current Position"
          periodLabel={getPeriodLabel(cardPeriod, cardCustomRange)}
          value={formatCurrencyWhole(currentPositionValue)}
          icon={<MdAssignment />}
          iconBg="bg-[#EBEBFF]"
          iconColor="text-[#8B8BFF]"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white p-6 rounded-[24px] border border-gray-100 shadow-sm">
          <div className="flex justify-between items-start gap-3 mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-700">
                Gross Margin Over time
              </h3>
              <p className="text-xs text-gray-400">
                {formatCurrencyWhole(totalTrendGM)}
                <Tag
                  color={chartTrendIsUp ? "green" : "red"}
                  className={`text-[10px] border-none mx-2 ${
                    chartTrendIsUp
                      ? "bg-green-50 text-green-600"
                      : "bg-red-50 text-red-600"
                  }`}
                >
                  {chartTrendText} {chartTrendIsUp ? "up" : "down"}
                </Tag>
                <span className="mr-1">{chartCompareLabel}</span>·
                <span className="ml-1">
                  {revenueBucketByMonth ? "GM by month" : "GM by day"}
                </span>
              </p>
            </div>
            <PeriodSelector
              period={revenuePeriod}
              onPeriodChange={setRevenuePeriod}
              customRange={revenueCustomRange}
              onCustomRangeChange={setRevenueCustomRange}
            />
          </div>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={lineData}>
                <defs>
                  <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#004aad" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#004aad" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#9ca3af" }} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#9ca3af" }} />
                <Tooltip formatter={(value: any) => formatCurrencyWhole(Number(value))} />
                <Area
                  type="monotone"
                  dataKey="gm"
                  name="Gross Margin"
                  stroke="#004aad"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorRev)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white p-6 rounded-[24px] border border-gray-100 shadow-sm flex flex-col">
          <div className="flex justify-between items-center gap-2 mb-4">
            <h3 className="text-lg font-semibold text-gray-700">
              Lead-wise Gross Margin
            </h3>
            <PeriodSelector
              period={statusPeriod}
              onPeriodChange={setStatusPeriod}
              customRange={statusCustomRange}
              onCustomRangeChange={setStatusCustomRange}
            />
          </div>
          <div className="flex-1 flex flex-col items-center justify-center">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={pieData} innerRadius={80} outerRadius={100} paddingAngle={5} dataKey="value">
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: any) => formatCurrencyWhole(Number(value))} />
              </PieChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-3 gap-4 w-full mt-6 text-center border-t pt-4">
              {pieData.map((item) => (
                <div key={item.name}>
                  <p className="text-xs text-gray-400 flex items-center justify-center gap-1">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }}></span>
                    {item.name}
                  </p>
                  <p className="text-lg font-bold text-gray-700">
                    {formatCurrencyWhole(item.value)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <ScorecardWidget />

      <div className="bg-white rounded-[24px] border border-gray-200 p-6 shadow-sm">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800">Recent Orders</h2>
          <div className="flex gap-2">
            <Button icon={<FilterOutlined />}>Filter</Button>
            <Button>See All</Button>
          </div>
        </div>
        <div className="rounded-xl overflow-hidden border border-gray-50">
          <Table
            dataSource={recentOrders}
            columns={columns}
            pagination={false}
            className="custom-dashboard-table"
            rowClassName={() => "h-20"}
            scroll={{ x: 1200 }}
            loading={loading}
          />
        </div>
      </div>
      </div>
    </div>
  );
};

const StatCard = ({
  title,
  periodLabel,
  value,
  trend,
  trendText,
  isUp,
  icon,
  iconBg,
  iconColor,
}: any) => {
  return (
    <div className="bg-white p-3 rounded-[24px] border border-gray-50 shadow-sm flex flex-col justify-between h-full">
      <div className="flex justify-between items-start mb-4">
        <div className="space-y-1">
          <p className="text-gray-400 text-[16px] font-medium leading-tight mb-3">{title}</p>
          {periodLabel ? <p className="text-[12px] text-gray-500 font-medium">{periodLabel}</p> : null}
          <h2 className="text-gray-800 text-[25px] font-semibold tracking-tight">{value}</h2>
        </div>
        <div className={`${iconBg} ${iconColor} p-3 rounded-3xl text-4xl flex items-center justify-center`}>
          {icon}
        </div>
      </div>
      <div className="flex items-center gap-2 mt-2">
        {typeof trend === "string" ? (
          <div
            className={`flex items-center gap-2 text-[17px] font-semibold ${
              isUp ? "text-emerald-500" : "text-rose-500"
            }`}
          >
            {isUp ? (
              <RiseOutlined size={18} className="text-[25px]" />
            ) : (
              <FallOutlined size={18} className="text-[25px]" />
            )}
            <span>{trend}</span>
          </div>
        ) : null}
        <span className="text-gray-400 text-[14px] font-normal">{trendText}</span>
      </div>
    </div>
  );
};

export default Dashboard;
